import React from 'react';
import { Link } from 'react-router-dom';
import { BanknoteIcon, CalendarClockIcon, ClockIcon, GraduationCapIcon, MonitorIcon, SearchXIcon } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { ProvenanceBadge } from '../../trust/ProvenanceBadge';
import { SaveButton } from '../SaveButton';
import { UniversityMark } from '../UniversityMark';
import { tuitionLabel } from '../CompareTray';
import { useDiscover } from '../../../contexts/DiscoverContext';
import { deadlineState } from '../../../utils/discover';
import { universityProfileById, scholarships } from '../../../data/admissions';
import { cn } from '../../../utils/cn';

/**
 * Programme results. The two compositions differ in density — editorial cards
 * with room to read, or a tighter workspace grid — but the facts shown, the
 * ordering and the actions are identical, because they come from one place.
 */
interface ProgrammeResultsProps {
  density: 'editorial' | 'compact';
}
export function ProgrammeResults({
  density
}: ProgrammeResultsProps) {
  const {
    programmes,
    isSaved,
    toggleSave,
    clearFilters
  } = useDiscover();
  if (programmes.length === 0) {
    return <div className="rounded-card border border-line bg-surface" data-id="element-1570" data-mp="2y1xnlog5h8j">
        <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-1572" data-mp="3u5we767agp0l" />} title="No programmes match what you asked for" description="Nothing here has been hidden — no published programme fits every filter at once. Widen the level, field or deadline and the list will fill again." action={<Button variant="secondary" onClick={clearFilters} data-id="element-1573" data-mp="3g6n8j0gpldav">
              Clear all filters
            </Button>} data-id="element-1571" data-mp="3u54ogc0dhli8" />
      </div>;
  }
  return <ul className={cn('grid gap-4', density === 'compact' ? 'md:grid-cols-2 xl:grid-cols-3' : 'lg:grid-cols-2')} data-id="element-1574" data-mp="1efa226z3excp">
      {programmes.map(programme => {
      const university = universityProfileById(programme.universityId);
      const state = deadlineState(programme.deadline);
      const funding = programme.scholarshipIds.map(id => scholarships.find(item => item.id === id)).filter(Boolean);
      return <li key={programme.id} className="group flex flex-col rounded-card border border-line bg-surface p-5 transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-[0_10px_30px_rgba(15,23,42,0.07)]" data-id="element-1575" data-mp="2ywjci1r1oczu">
            <div className="flex items-start gap-3" data-id="element-1576" data-mp="3dmqivj3s25k6">
              {university && <UniversityMark university={university} size="sm" data-id="element-1577" data-mp="3hnk6sch7zkd2" />}
              <div className="min-w-0 flex-1" data-id="element-1578" data-mp="3hlr1b0icf66n">
                <h3 className="text-body font-semibold text-ink" data-id="element-1579" data-mp="226aauaujyr7c">
                  <Link to={`/discover/programmes/${programme.id}`} className="rounded outline-none hover:underline focus-visible:underline" data-id="element-1580" data-mp="2296bi6xsbtzh">
                    {programme.title}
                  </Link>
                </h3>
                <p className="mt-0.5 truncate text-body-sm text-ink-muted" data-id="element-1581" data-mp="1tpjin94hqcx6">{university?.name}</p>
              </div>
              <SaveButton saved={isSaved('programme', programme.id)} onToggle={() => toggleSave('programme', programme.id)} subject={programme.title} data-id="element-1582" data-mp="28kpmbpg45znc" />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1583" data-mp="1aztlifd99f23">
              <Badge tone="brand" plain icon={<GraduationCapIcon className="h-3.5 w-3.5" data-id="element-1585" data-mp="1pog18i6so8tu" />} data-id="element-1584" data-mp="1axgyvowjygip">
                {programme.level}
              </Badge>
              <Badge plain data-id="element-1586" data-mp="1kwg4eym0tw1h">{programme.field}</Badge>
              <ProvenanceBadge provenance={programme.provenance} short data-id="element-1587" data-mp="x7wzmt26k1e5" />
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 text-body-sm" data-id="element-1588" data-mp="x5k8pqeg2goy">
              <div className="flex items-center gap-2 text-ink" data-id="element-1589" data-mp="s8k3titbzjzf">
                <BanknoteIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-1590" data-mp="3rnuir1o8g1wz" />
                <span className="cz-num truncate" data-id="element-1591" data-mp="3vnmxxx84t6bb">{tuitionLabel(programme)}</span>
              </div>
              <div className="flex items-center gap-2 text-ink-muted" data-id="element-1592" data-mp="4hv5gr6vthlc">
                <ClockIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-1593" data-mp="98jywc24gon4" />
                {programme.durationMonths} months
              </div>
              <div className="flex items-center gap-2 text-ink-muted" data-id="element-1594" data-mp="e7epi06bzhhs">
                <MonitorIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-1595" data-mp="j6845vxctejj" />
                {programme.deliveryMode}
              </div>
              <div className="flex items-center gap-2" data-id="element-1596" data-mp="n6ietlw3ct4q">
                <CalendarClockIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-1597" data-mp="s6muxat37luf" />
                <Badge tone={state.tone === 'neutral' ? 'neutral' : state.tone} plain data-id="element-1598" data-mp="2p115bg7hbkrr">
                  {state.label}
                </Badge>
              </div>
            </dl>

            {density === 'editorial' && <ul className="mt-4 space-y-1.5 border-t border-line pt-3" data-id="element-1599" data-mp="2mh4evwmq4900">
                {programme.requirements.slice(0, 2).map(requirement => <li key={requirement} className="text-body-sm text-ink-muted" data-id="element-1600" data-mp="2cpgsia50xi5e">
                    · {requirement}
                  </li>)}
                {programme.requirements.length > 2 && <li className="text-caption text-ink-subtle" data-id="element-1601" data-mp="35enuxk7lmrvx">
                    +{programme.requirements.length - 2} more requirements on the programme page
                  </li>}
              </ul>}

            {funding.length > 0 && <p className="mt-3 text-caption text-ink-muted" data-id="element-1602" data-mp="2rempdrge2wa4">
                <span className="font-semibold text-ink" data-id="element-1603" data-mp="2red1c9ewmz72">Linked funding:</span>{' '}
                {funding.map(item => item?.name).join(' · ')}
              </p>}

            <div className="mt-auto flex flex-wrap gap-2 pt-4" data-id="element-1604" data-mp="1fyodzgxqny5q">
              <Link to={`/discover/programmes/${programme.id}`} data-id="element-1605" data-mp="1hgwjj2qwlv3s">
                <Button size="sm" variant="primary" data-id="element-1606" data-mp="1hejz8ixob8nk">
                  View programme
                </Button>
              </Link>
              {university && <Link to={`/discover/universities/${university.id}`} data-id="element-1607" data-mp="17hnpyazyaihy">
                  <Button size="sm" variant="ghost" data-id="element-1608" data-mp="1ch0nbpog77ys">
                    {university.shortName}
                  </Button>
                </Link>}
            </div>
          </li>;
    })}
    </ul>;
}